import React, { useState, useEffect } from 'react';
import Modal from 'components/ui/Modal';
import PrimaryButton from 'components/button/PrimaryButton';
import SecondaryButton from 'components/button/SecondaryButton';
import { Icons } from 'components/Icons';
import './DownloadDocumentationModal.css';

/**
 * Download Documentation Modal
 * Allows users to select format (PDF/TXT) and content (sections, groups, APIs)
 */
const DownloadDocumentationModal = ({
  isOpen,
  onClose,
  sections = [],
  onDownload
}) => {
  const [format, setFormat] = useState('pdf'); // 'pdf' or 'txt'
  const [selectedSections, setSelectedSections] = useState({});
  const [selectedGroups, setSelectedGroups] = useState({});
  const [selectedItems, setSelectedItems] = useState({});

  // Initialize all sections as selected by default
  useEffect(() => {
    if (isOpen && sections.length > 0) {
      const initialSections = {};
      sections.forEach(section => {
        initialSections[section.id] = true;
      });
      setSelectedSections(initialSections);
      
      // Auto-select all groups and items when section is selected
      const initialGroups = {};
      const initialItems = {};
      sections.forEach(section => {
        if (section.items) {
          section.items.forEach(item => {
            if (item.groupHeader) {
              initialGroups[`${section.id}-${item.id}`] = true;
            } else {
              initialItems[`${section.id}-${item.id}`] = true;
            }
          });
        }
      });
      setSelectedGroups(initialGroups);
      setSelectedItems(initialItems);
    }
  }, [isOpen, sections]);

  // Handle section selection - auto-select all children
  const handleSectionToggle = (sectionId) => {
    const newValue = !selectedSections[sectionId];
    setSelectedSections(prev => ({
      ...prev,
      [sectionId]: newValue
    }));

    // Auto-select/deselect all groups and items in this section
    const section = sections.find(s => s.id === sectionId);
    if (section?.items) {
      const newGroups = { ...selectedGroups };
      const newItems = { ...selectedItems };
      
      section.items.forEach(item => {
        if (item.groupHeader) {
          newGroups[`${sectionId}-${item.id}`] = newValue;
        } else {
          newItems[`${sectionId}-${item.id}`] = newValue;
        }
      });
      
      setSelectedGroups(newGroups);
      setSelectedItems(newItems);
    }
  };

  // Handle group selection - auto-select all items in group
  const handleGroupToggle = (sectionId, groupId) => {
    const groupKey = `${sectionId}-${groupId}`;
    const newValue = !selectedGroups[groupKey];
    setSelectedGroups(prev => ({
      ...prev,
      [groupKey]: newValue
    }));

    // Auto-select/deselect all items in this group
    const section = sections.find(s => s.id === sectionId);
    if (section?.items) {
      let inGroup = false;
      const newItems = { ...selectedItems };
      
      section.items.forEach(item => {
        if (item.groupHeader && item.id === groupId) {
          inGroup = true;
        } else if (inGroup && item.groupHeader) {
          inGroup = false; // Next group reached
        } else if (inGroup && !item.groupHeader) {
          newItems[`${sectionId}-${item.id}`] = newValue;
        }
      });
      
      setSelectedItems(newItems);
    }
  };

  // Handle item selection
  const handleItemToggle = (sectionId, itemId) => {
    const itemKey = `${sectionId}-${itemId}`;
    setSelectedItems(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  };

  // Check if section is fully selected
  const isSectionFullySelected = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section?.items) return selectedSections[sectionId] || false;
    
    return section.items.every(item => {
      if (item.groupHeader) {
        return selectedGroups[`${sectionId}-${item.id}`] || false;
      } else {
        return selectedItems[`${sectionId}-${item.id}`] || false;
      }
    });
  };

  // Check if group is fully selected
  const isGroupFullySelected = (sectionId, groupId) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section?.items) return false;
    
    let inGroup = false;
    const groupItems = [];
    
    section.items.forEach(item => {
      if (item.groupHeader && item.id === groupId) {
        inGroup = true;
      } else if (inGroup && item.groupHeader) {
        inGroup = false; // Next group reached
      } else if (inGroup && !item.groupHeader) {
        groupItems.push(item);
      }
    });
    
    return groupItems.length > 0 && groupItems.every(item => 
      selectedItems[`${sectionId}-${item.id}`] || false
    );
  };

  // Get selected content structure
  const getSelectedContent = () => {
    const selectedContent = [];
    
    sections.forEach(section => {
      if (!selectedSections[section.id]) return;
      
      const sectionData = {
        id: section.id,
        title: section.title,
        items: []
      };
      
      if (section.items) {
        let currentGroup = null;
        let currentGroupItems = [];
        
        section.items.forEach(item => {
          if (item.groupHeader) {
            // Save previous group if it exists
            if (currentGroup && currentGroupItems.length > 0) {
              sectionData.items.push({
                ...currentGroup,
                items: currentGroupItems
              });
            }
            // Start new group
            const groupKey = `${section.id}-${item.id}`;
            if (selectedGroups[groupKey]) {
              currentGroup = item;
              currentGroupItems = [];
            } else {
              currentGroup = null;
              currentGroupItems = [];
            }
          } else {
            const itemKey = `${section.id}-${item.id}`;
            if (selectedItems[itemKey]) {
              if (currentGroup) {
                currentGroupItems.push(item);
              } else {
                // Item without group
                sectionData.items.push(item);
              }
            }
          }
        });
        
        // Don't forget the last group
        if (currentGroup && currentGroupItems.length > 0) {
          sectionData.items.push({
            ...currentGroup,
            items: currentGroupItems
          });
        }
      }
      
      if (sectionData.items.length > 0) {
        selectedContent.push(sectionData);
      }
    });
    
    return selectedContent;
  };

  const handleDownload = () => {
    const content = getSelectedContent();
    if (content.length === 0) {
      alert('Please select at least one item to download.');
      return;
    }
    
    if (onDownload) {
      onDownload(format, content);
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Download Documentation"
      size="lg"
      className="download-docs-modal"
    >
      <div className="download-docs-content">
        {/* Format Selection */}
        <div className="download-docs-section">
          <h4 className="download-docs-section-title">Format</h4>
          <div className="download-docs-format-options">
            <label className="download-docs-format-option">
              <input
                type="radio"
                name="format"
                value="pdf"
                checked={format === 'pdf'}
                onChange={(e) => setFormat(e.target.value)}
              />
              <span>PDF (with cover page)</span>
            </label>
            <label className="download-docs-format-option">
              <input
                type="radio"
                name="format"
                value="txt"
                checked={format === 'txt'}
                onChange={(e) => setFormat(e.target.value)}
              />
              <span>Text File</span>
            </label>
          </div>
        </div>

        {/* Content Selection */}
        <div className="download-docs-section">
          <h4 className="download-docs-section-title">Content</h4>
          <div className="download-docs-content-tree">
            {sections.map(section => {
              const sectionSelected = selectedSections[section.id] || false;
              // eslint-disable-next-line no-unused-vars
              const sectionFullySelected = isSectionFullySelected(section.id);
              
              return (
                <div key={section.id} className="download-docs-tree-item">
                  <label className="download-docs-tree-label">
                    <input
                      type="checkbox"
                      checked={sectionSelected}
                      onChange={() => handleSectionToggle(section.id)}
                    />
                    <span className="download-docs-tree-title">{section.title}</span>
                  </label>
                  
                  {section.items && section.items.length > 0 && (
                    <div className="download-docs-tree-children">
                      {(() => {
                        // Group items by group headers
                        const groupedItems = [];
                        let currentGroup = null;
                        let currentGroupItems = [];
                        
                        section.items.forEach(item => {
                          if (item.groupHeader) {
                            if (currentGroup) {
                              groupedItems.push({
                                group: currentGroup,
                                items: currentGroupItems
                              });
                            }
                            currentGroup = item;
                            currentGroupItems = [];
                          } else {
                            currentGroupItems.push(item);
                          }
                        });
                        
                        if (currentGroup) {
                          groupedItems.push({
                            group: currentGroup,
                            items: currentGroupItems
                          });
                        } else if (currentGroupItems.length > 0) {
                          groupedItems.push({
                            group: null,
                            items: currentGroupItems
                          });
                        }
                        
                        return groupedItems.map(({ group, items }, groupIndex) => {
                          const groupKey = group ? `${section.id}-${group.id}` : `${section.id}-ungrouped-${groupIndex}`;
                          const groupSelected = group ? (selectedGroups[groupKey] || false) : true;
                          // eslint-disable-next-line no-unused-vars
                          const groupFullySelected = group ? isGroupFullySelected(section.id, group.id) : true;
                          
                          return (
                            <div key={groupKey} className="download-docs-tree-item download-docs-tree-item-nested">
                              {group && (
                                <label className="download-docs-tree-label">
                                  <input
                                    type="checkbox"
                                    checked={groupSelected}
                                    onChange={() => handleGroupToggle(section.id, group.id)}
                                  />
                                  <span className="download-docs-tree-title download-docs-tree-group">
                                    {group.title}
                                  </span>
                                </label>
                              )}
                              
                              {items.length > 0 && (
                                <div className="download-docs-tree-children">
                                  {items.map(item => {
                                    const itemKey = `${section.id}-${item.id}`;
                                    const itemSelected = selectedItems[itemKey] || false;
                                    
                                    return (
                                      <div key={item.id} className="download-docs-tree-item download-docs-tree-item-nested">
                                        <label className="download-docs-tree-label">
                                          <input
                                            type="checkbox"
                                            checked={itemSelected}
                                            onChange={() => handleItemToggle(section.id, item.id)}
                                          />
                                          <span className="download-docs-tree-title download-docs-tree-api">
                                            {item.title}
                                          </span>
                                        </label>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      <div className="download-docs-footer">
        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
        <PrimaryButton onClick={handleDownload}>
          <Icons.Download size={16} style={{ marginRight: '8px' }} />
          Download {format.toUpperCase()}
        </PrimaryButton>
      </div>
    </Modal>
  );
};

export default DownloadDocumentationModal;

